import { Injectable } from '@angular/core';
import {
  PlotlyTrace,
  PlotlyConfig,
} from '@app/types';
import {
  ChartType,
  IChartTypeConfig,
  ILineChartConfig,
  IScatterChartConfig,
  IBarChartConfig,
  IStackedBarChartConfig,
  IPieChartConfig,
  IHistogramChartConfig,
  IBoxChartConfig,
  IMultipleBoxChartConfig,
  IMultipleViolinChartConfig,
  IHeatmapChartConfig,
} from '@common/models/Chat/IChartConfig';
import { IChatInteractionResponseTable } from '@common/models/Chat/IChatResponse';

const DEFAULT_CHART_COLOR = '#3d4a5d';

@Injectable({
  providedIn: 'root',
})
export class PlotterService {

  private readonly plotFunctions: Record<ChartType, (data: Record<string, (string | number)[]>, config: IChartTypeConfig) => PlotlyTrace[]> = {
    bar: this.plotBar.bind(this),
    stacked_bar_plot: this.plotStackedBar.bind(this),
    pie: this.plotPie.bind(this),
    line: this.plotLine.bind(this),
    scatter: this.plotScatter.bind(this),
    histogram: this.plotHistogram.bind(this),
    box: this.plotBox.bind(this),
    multiple_box_plot: this.plotMultiBox.bind(this),
    multiple_violin_plot: this.plotMultiViolin.bind(this),
    heatmap: this.plotHeatmap.bind(this),
  };

  /**
   * Converts table format (headers + rows) to column-based object
   */
  tableToColumnData(table: IChatInteractionResponseTable): Record<string, (string | number)[]> {
    const data: Record<string, (string | number)[]> = {};
    table.headers.forEach((header, index) => {
      data[header] = table.rows.map((row) => {
        const val = row[index];
        const num = Number(val);
        return Number.isNaN(num) ? val : num;
      });
    });
    return data;
  }

  /**
   * Main method to generate Plotly config from table and chart config
   */
  plot(table: IChatInteractionResponseTable, chartType: ChartType, chartTitle: string, chartConfig: IChartTypeConfig): PlotlyConfig | null {
    if (!this.plotFunctions[chartType]) {
      console.warn(`Unsupported chart type: ${chartType}`);
      return null;
    }

    const data = this.tableToColumnData(table);
    const traces = this.plotFunctions[chartType](data, chartConfig);
    const axisLabels = this.getAxisLabels(chartType, chartConfig);
    const layoutConfig = this.getLayoutConfig(chartType, data, chartConfig);

    return {
      data: traces,
      layout: {
        title: { text: chartTitle },
        ...layoutConfig,
        xaxis: {
          ...(layoutConfig.xaxis as object || {}),
          ...(axisLabels.xaxis || {}),
        },
        yaxis: {
          ...(layoutConfig.yaxis as object || {}),
          ...(axisLabels.yaxis || {}),
        },
      },
    };
  }

  private getLayoutConfig(chartType: ChartType, data: Record<string, (string | number)[]>, config: IChartTypeConfig): Record<string, unknown> {
    const baseLayout: Record<string, unknown> = {
      margin: { l: 60, r: 30, t: 50, b: 80 },
      legend: {
        orientation: 'h',
        yanchor: 'top',
        y: -0.2,
        xanchor: 'center',
        x: 0.5,
      },
    };

    if (chartType === 'bar' || chartType === 'stacked_bar_plot') {
      const cfg = config as IBarChartConfig | IStackedBarChartConfig;
      const xColumn = 'x' in cfg ? cfg.x : (cfg as IStackedBarChartConfig).columnsToGroupby?.[0];
      const xValues = xColumn ? data[xColumn] : [];
      const maxLabelLength = Math.max(...xValues.map(v => String(v).length), 0);
      const numCategories = xValues.length;

      // Increase bottom margin for long labels or many categories
      const bottomMargin = Math.min(150, Math.max(80, maxLabelLength * 5 + numCategories * 0.5));

      return {
        ...baseLayout,
        margin: { l: 60, r: 30, t: 50, b: bottomMargin },
        xaxis: {
          ...((baseLayout.xaxis as object) || {}),
          tickangle: numCategories > 10 || maxLabelLength > 10 ? -45 : 0,
          automargin: true,
        },
        legend: {
          orientation: 'h',
          yanchor: 'top',
          y: -0.3,
          xanchor: 'center',
          x: 0.5,
        },
      };
    }

    return baseLayout;
  }

  private getAxisLabels(chartType: ChartType, config: IChartTypeConfig): { xaxis?: { title: { text: string }; automargin?: boolean }; yaxis?: { title: { text: string }; automargin?: boolean } } {
    switch (chartType) {
      case 'bar': {
        const cfg = config as IBarChartConfig;
        return {
          xaxis: { title: { text: cfg.x }, automargin: true },
          yaxis: { title: { text: cfg.height ?? cfg.y }, automargin: true },
        };
      }
      case 'stacked_bar_plot': {
        const cfg = config as IStackedBarChartConfig;
        return {
          xaxis: { title: { text: cfg.columnsToGroupby?.[0] ?? '' }, automargin: true },
          yaxis: { title: { text: cfg.y }, automargin: true },
        };
      }
      case 'line': {
        const cfg = config as ILineChartConfig;
        const yLabel = Array.isArray(cfg.y) ? cfg.y.join(', ') : cfg.y;
        return {
          xaxis: { title: { text: cfg.x } },
          yaxis: { title: { text: yLabel } },
        };
      }
      case 'scatter': {
        const cfg = config as IScatterChartConfig;
        return {
          xaxis: { title: { text: cfg.x } },
          yaxis: { title: { text: cfg.y } },
        };
      }
      case 'histogram': {
        const cfg = config as IHistogramChartConfig;
        return {
          xaxis: { title: { text: cfg.x } },
          yaxis: { title: { text: 'Count' } },
        };
      }
      case 'box': {
        const cfg = config as IBoxChartConfig;
        return {
          yaxis: { title: { text: cfg.x } },
        };
      }
      case 'multiple_box_plot': {
        const cfg = config as IMultipleBoxChartConfig;
        return {
          xaxis: { title: { text: cfg.x } },
          yaxis: { title: { text: cfg.y } },
        };
      }
      case 'multiple_violin_plot': {
        const cfg = config as IMultipleViolinChartConfig;
        return {
          xaxis: { title: { text: cfg.x } },
          yaxis: { title: { text: cfg.y } },
        };
      }
      default:
        return {};
    }
  }

  private plotLine(data: Record<string, (string | number)[]>, config: IChartTypeConfig): PlotlyTrace[] {
    const cfg = config as ILineChartConfig;
    const x = cfg.x;
    const y = cfg.y;
    const yColumns = Array.isArray(y) ? y : [y];

    return yColumns.map((yCol, index) => ({
      x: data[x],
      y: data[yCol],
      type: 'scatter' as const,
      mode: 'lines' as const,
      name: yCol,
      line: {
        width: cfg.linewidth ?? 2,
        color: Array.isArray(cfg.color) ? cfg.color[index] : (index === 0 ? (cfg.color ?? DEFAULT_CHART_COLOR) : undefined),
      },
    }));
  }

  private plotScatter(data: Record<string, (string | number)[]>, config: IChartTypeConfig): PlotlyTrace[] {
    const cfg = config as IScatterChartConfig;
    const x = cfg.x;
    const y = cfg.y;

    return [{
      x: data[x],
      y: data[y],
      type: 'scatter',
      mode: 'markers',
      marker: {
        size: cfg.markerSize ?? 10,
        opacity: cfg.alpha ?? 0.8,
      },
    }];
  }

  private plotBar(data: Record<string, (string | number)[]>, config: IChartTypeConfig): PlotlyTrace[] {
    const cfg = config as IBarChartConfig;
    const x = cfg.x;
    const y = cfg.height ?? cfg.y;

    return [{
      x: data[x],
      y: data[y],
      type: 'bar',
      marker: {
        color: cfg.color ?? cfg.colors?.[0] ?? DEFAULT_CHART_COLOR,
      },
    }];
  }

  private plotStackedBar(data: Record<string, (string | number)[]>, config: IChartTypeConfig): PlotlyTrace[] {
    const cfg = config as IStackedBarChartConfig;
    const groupCols = cfg.columnsToGroupby ?? [];
    const y = cfg.y;

    if (groupCols.length < 2) {
      return this.plotBar(data, config);
    }

    const uniqueGroups = [...new Set(data[groupCols[1]] as string[])];
    const traces: PlotlyTrace[] = [];

    uniqueGroups.forEach((group, idx) => {
      const xVals: (string | number)[] = [];
      const yVals: (string | number)[] = [];

      data[groupCols[0]].forEach((val, i) => {
        if (data[groupCols[1]][i] === group) {
          xVals.push(val);
          yVals.push(data[y][i]);
        }
      });

      traces.push({
        x: xVals,
        y: yVals,
        type: 'bar',
        name: String(group),
        marker: { color: cfg.colors?.[idx] },
      });
    });

    return traces;
  }

  private plotHistogram(data: Record<string, (string | number)[]>, config: IChartTypeConfig): PlotlyTrace[] {
    const cfg = config as IHistogramChartConfig;
    const x = cfg.x;

    return [{
      x: data[x],
      type: 'histogram',
      marker: {
        color: cfg.color ?? DEFAULT_CHART_COLOR,
        line: { color: 'white', width: 1 },
        opacity: 0.9,
      },
    }];
  }

  private plotPie(data: Record<string, (string | number)[]>, config: IChartTypeConfig): PlotlyTrace[] {
    const cfg = config as IPieChartConfig;
    const values = cfg.x;
    const labels = cfg.labels;

    return [{
      values: data[values],
      labels: data[labels],
      type: 'pie',
      marker: {
        color: cfg.colors,
      },
    }];
  }

  private plotBox(data: Record<string, (string | number)[]>, config: IChartTypeConfig): PlotlyTrace[] {
    const cfg = config as IBoxChartConfig;
    const x = cfg.x;

    return [{
      y: data[x],
      type: 'box',
      marker: {
        color: DEFAULT_CHART_COLOR,
      },
    }];
  }

  private plotMultiBox(data: Record<string, (string | number)[]>, config: IChartTypeConfig): PlotlyTrace[] {
    const cfg = config as IMultipleBoxChartConfig;
    const x = cfg.x;
    const y = cfg.y;

    return [{
      x: data[x],
      y: data[y],
      type: 'box',
      marker: {
        color: DEFAULT_CHART_COLOR,
      },
    }];
  }

  private plotMultiViolin(data: Record<string, (string | number)[]>, config: IChartTypeConfig): PlotlyTrace[] {
    const cfg = config as IMultipleViolinChartConfig;
    const x = cfg.x;
    const y = cfg.y;

    return [{
      x: data[x],
      y: data[y],
      type: 'violin',
      box: { visible: true },
      points: 'all',
      marker: {
        color: DEFAULT_CHART_COLOR,
      },
    }];
  }

  private plotHeatmap(data: Record<string, (string | number)[]>, config: IChartTypeConfig): PlotlyTrace[] {
    const cfg = config as IHeatmapChartConfig;
    const headers = Object.keys(data);

    if (cfg.normalHeatmap?.isNormalHeatmapPlot) {
      const idx = cfg.normalHeatmap.index;
      const col = cfg.normalHeatmap.columns;
      const val = cfg.normalHeatmap.values;

      const uniqueRows = [...new Set(data[idx] as string[])];
      const uniqueCols = [...new Set(data[col] as string[])];

      const z: number[][] = uniqueRows.map((row) =>
        uniqueCols.map((c) => {
          const i = data[idx].findIndex((v, index) => v === row && data[col][index] === c);
          return i >= 0 ? Number(data[val][i]) : 0;
        })
      );

      return [{
        z,
        x: uniqueCols,
        y: uniqueRows,
        type: 'heatmap',
        colorscale: cfg.cmap ?? 'YlGnBu',
        showscale: true,
      }];
    }

    // Default: correlation heatmap of numeric columns
    const numericHeaders = headers.filter((h) =>
      data[h].every((v) => typeof v === 'number' || !Number.isNaN(Number(v)))
    );

    const cols = cfg.correlation?.columns ?? numericHeaders;
    const n = cols.length;
    const z: number[][] = [];

    for (let i = 0; i < n; i++) {
      const row: number[] = [];
      for (let j = 0; j < n; j++) {
        row.push(this.correlation(data[cols[i]] as number[], data[cols[j]] as number[]));
      }
      z.push(row);
    }

    return [{
      z,
      x: cols,
      y: cols,
      type: 'heatmap',
      colorscale: cfg.cmap ?? 'YlGnBu',
      showscale: true,
    }];
  }

  private correlation(arr1: number[], arr2: number[]): number {
    const n = arr1.length;
    if (n === 0) return 0;

    const mean1 = arr1.reduce((a, b) => a + b, 0) / n;
    const mean2 = arr2.reduce((a, b) => a + b, 0) / n;

    let num = 0;
    let den1 = 0;
    let den2 = 0;

    for (let i = 0; i < n; i++) {
      const d1 = arr1[i] - mean1;
      const d2 = arr2[i] - mean2;
      num += d1 * d2;
      den1 += d1 * d1;
      den2 += d2 * d2;
    }

    const den = Math.sqrt(den1) * Math.sqrt(den2);
    return den === 0 ? 0 : Number((num / den).toFixed(2));
  }
}
